import React, { useState } from 'react';
import { toast } from 'sonner';
import { aiInterviewsAPI, errMsg, SEGMENT_LABELS } from '../../../lib/aiInterviewApi';
import { fmtDate, Pill, Section, StatusPill } from '../recruiter/common';

const PENDING_COPY = {
  invited: ['Waiting for the candidate', 'The invite was sent. The link has not been opened yet.'],
  opened: ['Candidate opened the link', 'They have not given consent yet.'],
  consented: ['Candidate consented', 'They are doing the tech and ID checks, or have not started the interview yet.'],
  starting: ['Starting', 'The interview is starting.'],
  in_progress: ['Interview in progress', 'The transcript below updates as the candidate answers.'],
  interrupted: ['Interrupted', 'The candidate was disconnected for more than 5 minutes. Reopen the interview to let them continue from their last answer.'],
  completed: ['Scoring', 'The interview finished. The AI report appears here in a minute or two.'],
  scoring: ['Scoring', 'The AI report appears here in a minute or two.'],
  score_error: ['Scoring failed', 'Use Rescore to try again.'],
  declined: ['Declined', 'The candidate declined the AI interview and asked for a human interview.'],
  expired: ['Expired', 'The invite expired before the interview. Extend it to reactivate the link.'],
  revoked: ['Revoked', 'This invite was withdrawn.'],
  data_deleted: ['Data deleted', 'The interview data was deleted on request or under the retention policy.'],
};

export function PendingPanel({ d }) {
  const [title, body] = PENDING_COPY[d.status] || [d.status, ''];
  return (
    <Section testId="report-pending-panel">
      <div className="iv-row" style={{ justifyContent: 'space-between' }}>
        <div><h2 className="iv-h2" style={{ margin: 0 }} data-testid="report-pending-title">{title}</h2><p className="iv-small">{body}</p></div>
        <StatusPill status={d.status} />
      </div>
      {d.score_error && <div className="iv-note is-bad" style={{ marginTop: '0.6rem' }}>{d.score_error}</div>}
      {d.blueprint_status !== 'ready' && <div className="iv-note" style={{ marginTop: '0.6rem' }} data-testid="report-blueprint-status">Blueprint: {d.blueprint_status === 'failed' ? `failed — ${d.blueprint_error || 'unknown error'}` : 'AI is preparing the personalised blueprint from the résumé and JD…'}</div>}
    </Section>
  );
}

const Card = ({ title, children, testId }) => (
  <div className="iv-card" style={{ marginBottom: 0 }} data-testid={testId}><div className="iv-eyebrow" style={{ marginBottom: '0.5rem' }}>{title}</div>{children}</div>
);
const KV = ({ k, v }) => <div className="iv-row iv-small" style={{ justifyContent: 'space-between' }}><span>{k}</span><b style={{ color: '#002A38', fontWeight: 600 }}>{v ?? '—'}</b></div>;

function Notes({ d, reload }) {
  const [note, setNote] = useState('');
  const add = async () => {
    try {
      await aiInterviewsAPI.annotate(d.id, { note });
      setNote('');
      reload();
    } catch (e) {
      toast.error(errMsg(e, 'Could not add the note'));
    }
  };
  return (
    <Card title="Team notes" testId="report-notes">
      {(d.annotations || []).map((a) => <div key={`${a.at}-${a.by}`} className="iv-small" style={{ marginBottom: 6 }}><b>{a.by}</b> · {fmtDate(a.at)}<div>{a.note}</div></div>)}
      {(d.overrides || []).map((o, i) => <div key={`o${i}`} className="iv-small" style={{ marginBottom: 6 }}><b>{o.by}</b> overrode {o.competency}: {o.from} → {o.to} · {fmtDate(o.at)}<div>{o.note}</div></div>)}
      <div className="iv-row" style={{ marginTop: '0.4rem' }}>
        <input className="iv-input" style={{ flex: 1 }} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note for your team" data-testid="annotation-input" />
        <button type="button" className="iv-btn iv-btn-primary iv-btn-sm" disabled={!note.trim()} onClick={add} data-testid="annotation-add-btn">Add</button>
      </div>
    </Card>
  );
}

export function DetailsPanel({ d, reload }) {
  const t = d.tech || {};
  return (
    <div style={{ display: 'grid', gap: '0.75rem', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', marginBottom: '1rem' }} data-testid="report-details">
      <Card title="Consent" testId="report-consent">
        {d.consent ? <><KV k="Decision" v={d.consent.decision} /><KV k="Scopes" v={(d.consent.scopes || []).join(', ')} /><KV k="Version" v={d.consent.version} /><KV k="When" v={fmtDate(d.consent.ts)} /></> : <p className="iv-small">Not yet given.</p>}
      </Card>
      <Card title="Accommodations" testId="report-accommodations">
        {d.accommodations?.length ? d.accommodations.map((a) => <KV key={a.id} k={a.type.replace(/_/g, ' ')} v={a.status.replace('_', ' ')} />) : <p className="iv-small">None requested.</p>}
        {d.extra_minutes > 0 && <KV k="Extra time" v={`+${d.extra_minutes} min`} />}
      </Card>
      <Card title="Session" testId="report-session">
        <KV k="Duration" v={`${d.duration_min} min${d.extra_minutes ? ` +${d.extra_minutes}` : ''}`} />
        <KV k="Minutes used" v={d.minutes_used} />
        <KV k="Ended" v={d.end_reason ? d.end_reason.replace(/_/g, ' ') : null} />
        <KV k="Device" v={[t.device, t.browser].filter(Boolean).join(' · ')} />
        <KV k="Network" v={t.bandwidth_kbps ? `${(t.bandwidth_kbps / 1000).toFixed(1)} Mbps` : null} />
        <KV k="Video" v={t.video_height ? `${t.video_height}p · ${t.fps || '?'} fps` : null} />
        <KV k="Interruptions" v={(d.interruptions || []).length} />
      </Card>
      <Card title="Status history" testId="report-history">
        {(d.status_history || []).slice(-8).map((h, i) => (
          <div key={`${h.at}-${h.status}`} className="iv-hist iv-small" data-testid={`report-history-row-${i}`}><span>{h.status.replace(/_/g, ' ')}</span><b>{`${fmtDate(h.at)}${h.by ? ` · ${h.by}` : ''}`}</b></div>
        ))}
        {d.retention?.recordings_until && <p className="iv-small" style={{ marginTop: 6 }}>Recording kept until {fmtDate(d.retention.recordings_until, false)} · biometrics until {fmtDate(d.retention.biometric_until, false)}</p>}
      </Card>
      <Notes d={d} reload={reload} />
    </div>
  );
}

const lines = (s) => s.split('\n').map((x) => x.trim()).filter(Boolean);

function BlueprintEditor({ bp, onCancel, onSave }) {
  const [summary, setSummary] = useState(bp.role_summary || '');
  const [claims, setClaims] = useState((bp.resume_claims_to_verify || []).map((c) => c.claim).join('\n'));
  const [gaps, setGaps] = useState((bp.gaps_or_risks || []).join('\n'));
  const [seeds, setSeeds] = useState(bp.seed_questions || []);
  const save = () => {
    const prev = Object.fromEntries((bp.resume_claims_to_verify || []).map((c) => [c.claim, c]));
    onSave({
      ...bp, role_summary: summary, gaps_or_risks: lines(gaps),
      resume_claims_to_verify: lines(claims).map((c) => prev[c] || { claim: c, source: 'recruiter', probe_angles: [] }),
      seed_questions: seeds.filter((q) => q.question.trim().length >= 5),
    });
  };
  return (
    <div style={{ display: 'grid', gap: '0.7rem' }} data-testid="blueprint-editor">
      <label className="iv-small">Role summary<textarea className="iv-textarea" rows={3} value={summary} onChange={(e) => setSummary(e.target.value)} data-testid="blueprint-summary-input" /></label>
      <label className="iv-small">Résumé claims to verify (one per line)<textarea className="iv-textarea" rows={4} value={claims} onChange={(e) => setClaims(e.target.value)} data-testid="blueprint-claims-input" /></label>
      <label className="iv-small">Gaps or risks to explore (one per line)<textarea className="iv-textarea" rows={3} value={gaps} onChange={(e) => setGaps(e.target.value)} data-testid="blueprint-gaps-input" /></label>
      <div className="iv-small">Seed questions</div>
      {/* Fixed-length list that never reorders, so the position is a stable key. */}
      {seeds.map((q, i) => (
        <div key={`seed-${i}`} className="iv-row" style={{ alignItems: 'flex-start' }}>
          <span className="iv-pill iv-tone-sky" style={{ marginTop: 8 }}>{q.competency}</span>
          <textarea className="iv-textarea" rows={2} style={{ flex: 1 }} value={q.question} onChange={(e) => setSeeds(seeds.map((s, j) => (j === i ? { ...s, question: e.target.value } : s)))} data-testid={`blueprint-seed-${i}`} />
        </div>
      ))}
      <div className="iv-row-end">
        <button type="button" className="iv-btn iv-btn-ghost" onClick={onCancel}>Cancel</button>
        <button type="button" className="iv-btn iv-btn-primary" onClick={save} data-testid="blueprint-save-btn">Save blueprint</button>
      </div>
    </div>
  );
}

export function BlueprintPanel({ d, reload }) {
  const [editing, setEditing] = useState(false);
  const bp = d.blueprint;
  const editable = ['invited', 'opened', 'consented'].includes(d.status);
  const save = async (next) => {
    try {
      await aiInterviewsAPI.saveBlueprint(d.id, next);
      toast.success('Blueprint saved');
      setEditing(false);
      reload();
    } catch (e) {
      toast.error(errMsg(e, 'Could not save the blueprint'));
    }
  };
  const regen = async () => {
    try { await aiInterviewsAPI.regenerateBlueprint(d.id); toast.success('Regenerating the blueprint…'); reload(); } catch (e) { toast.error(errMsg(e, 'Could not regenerate')); }
  };
  const right = editable && (
    <div className="iv-row">
      <button type="button" className="iv-btn iv-btn-ghost iv-btn-sm" onClick={regen} data-testid="blueprint-regenerate-btn">Regenerate</button>
      {bp && !editing && <button type="button" className="iv-btn iv-btn-primary iv-btn-sm" onClick={() => setEditing(true)} data-testid="blueprint-edit-btn">Edit</button>}
    </div>
  );
  return (
    <Section title={`Interview blueprint${d.blueprint_version ? ` · v${d.blueprint_version}` : ''}`} testId="blueprint-panel" right={right}>
      {!bp ? <p className="iv-small">Not generated yet.</p> : editing ? <BlueprintEditor bp={bp} onCancel={() => setEditing(false)} onSave={save} /> : (
        <details open={editable}>
          <summary className="iv-small" style={{ cursor: 'pointer', fontWeight: 700 }} data-testid="blueprint-toggle">Role summary, claims to verify, risks and seed questions</summary>
          <p className="iv-sub" style={{ fontSize: '0.88rem', marginTop: '0.5rem' }}>{bp.role_summary} <Pill tone="sky">{bp.seniority_level}</Pill></p>
          <div className="iv-eyebrow" style={{ marginTop: '0.75rem' }}>Segment plan</div>
          <div className="iv-row iv-small">{(bp.segment_plan || []).map((s) => <span key={s.segment}>{SEGMENT_LABELS[s.segment]} {s.minutes}m</span>)}</div>
          <div className="iv-eyebrow" style={{ marginTop: '0.75rem' }}>Résumé claims to verify</div>
          <ul className="iv-small" style={{ paddingLeft: '1rem' }}>{(bp.resume_claims_to_verify || []).map((c) => <li key={c.claim}><b>{c.claim}</b>{c.probe_angles?.length ? ` — probe: ${c.probe_angles.join('; ')}` : ''}</li>)}</ul>
          {bp.gaps_or_risks?.length > 0 && <><div className="iv-eyebrow" style={{ marginTop: '0.75rem' }}>Gaps or risks</div><ul className="iv-small" style={{ paddingLeft: '1rem' }}>{bp.gaps_or_risks.map((g) => <li key={g}>{g}</li>)}</ul></>}
          <div className="iv-eyebrow" style={{ marginTop: '0.75rem' }}>Seed questions</div>
          <ol className="iv-small" style={{ paddingLeft: '1rem' }}>{(bp.seed_questions || []).map((q) => <li key={`${q.competency}-${q.question}`}><span className="iv-pill iv-tone-sky">{q.competency}</span> {q.question}</li>)}</ol>
        </details>
      )}
    </Section>
  );
}
